/**
 * A GET that can report its download and be called off (#1069 Story 7.5,
 * Task 6.1, AC 19; D11, C10).
 *
 * The sibling of `postWithProgress.ts`, not an edit to it: that module is the
 * ONE call that carries files up, this is the ONE call that carries a zip
 * down. Same reasons for not going through `api.ts` (no abort, and a progress
 * that is estimated rather than measured), same injectable XHR so the module
 * is testable without a DOM.
 *
 * ⚠️ THE PROGRESS LISTENER IS ON THE RESPONSE, NOT ON `xhr.upload`. And it
 * hands the caller RAW BYTES, not a percent: a streamed zip is chunked, so
 * `event.total` is 0 and `lengthComputable` is false on every event. The
 * caller (the hook) owns the only honest denominator, the listing's own
 * `sizeBytes` sum, and does the arithmetic itself (D12).
 *
 * ⚠️ `responseType = 'blob'`, SO THE BODY NEVER PASSES THROUGH A STRING.
 * `saveBase64File`'s web branch boxes one JavaScript number per byte (C9);
 * for a 200 MB zip that is a dead tab. The Blob goes straight to an
 * `<a download>` through `saveBlobFile`. The cost is that an error body has
 * to be read back out of the Blob asynchronously; `readErrorBody` does that.
 *
 * ⚠️ WEB ONLY, BY CONSTRUCTION. A Blob is a web thing. Native streams the
 * response to disk through `react-native-blob-util` (`downloadToDevice` in
 * `attachmentService.ts`), and `useSaveAllFiles` picks between the two by
 * reading `Platform.OS` in its body (7.2 D9).
 *
 * @see src/services/correspondence/__tests__/getWithProgress.test.ts
 */

/** A download in flight. */
export interface ProgressDownload {
  promise: Promise<Blob>;
  /** Call it off. Nothing lands anywhere. */
  abort: () => void;
}

/**
 * Mirrors `UploadError` (`postWithProgress.ts`). The two could become one
 * `TransferError` when either is next touched; that is in the ledger, not
 * here (D11).
 */
export class DownloadError extends Error {
  /** 0 for an abort or a network failure. */
  readonly status: number;
  /**
   * ⚠️ THE CALLER MUST BE ABLE TO TELL AN ABORT FROM A FAILURE WITHOUT READING
   * THE MESSAGE. An abort says nothing (D14: a cancelled zip lands nowhere);
   * a failure lands on the panel's line or opens the retry dialog.
   */
  readonly aborted: boolean;

  constructor(status: number, message: string, aborted = false) {
    super(message);
    this.name = 'DownloadError';
    this.status = status;
    this.aborted = aborted;
  }
}

/**
 * A non-2xx body is JSON with an `error` when the server had a reason (the
 * 413's `zipTooLargeReason`, the 404's `Not found`), and anything at all when
 * a gateway answered instead. With `responseType = 'blob'` the body is a
 * Blob; the fakes in the suite hand back a string. Both are read.
 */
const readErrorBody = async (response: unknown): Promise<string | null> => {
  if (typeof response === 'string') return response;
  if (
    response &&
    typeof response === 'object' &&
    typeof (response as { text?: unknown }).text === 'function'
  ) {
    try {
      return await (response as { text: () => Promise<string> }).text();
    } catch {
      return null;
    }
  }
  return null;
};

const reasonFrom = (body: string | null, status: number): string => {
  if (body !== null) {
    try {
      const parsed = JSON.parse(body) as { error?: unknown };
      if (typeof parsed?.error === 'string' && parsed.error)
        return parsed.error;
    } catch {
      // A gateway's HTML error page.
    }
  }
  return `Request failed (${status})`;
};

/**
 * `Content-Type` is compared on its media type alone: `application/zip` and
 * `application/zip; charset=binary` are the same answer.
 */
const mediaTypeOf = (header: string | null): string =>
  (header ?? '').split(';')[0].trim().toLowerCase();

export const getBlobWithProgress = (args: {
  url: string;
  headers: Record<string, string>;
  /** Raw bytes received so far, straight from the event (C10, D12). */
  onProgress?: (receivedBytes: number) => void;
  /**
   * When set, a 2xx whose `Content-Type` is another media type is a failure
   * (`DownloadError(status, 'Unexpected response')`), not a Blob (review
   * patch P10). An SSO redirect that lands on a login page answers `200
   * text/html`, and without this the page was saved as the zip.
   */
  expectedContentType?: string;
  /** Test seam. Defaults to the platform's own. */
  xhrFactory?: () => XMLHttpRequest;
}): ProgressDownload => {
  const { url, headers, onProgress, expectedContentType, xhrFactory } = args;
  const xhr = (xhrFactory ?? (() => new XMLHttpRequest()))();

  let settled = false;

  const promise = new Promise<Blob>((resolve, reject) => {
    const finish = (fn: () => void) => {
      // Once settled, later events are noise: an `abort` is commonly followed
      // by a `load` on some stacks, and that must not resolve a cancelled
      // download.
      if (settled) return;
      settled = true;
      fn();
    };

    xhr.addEventListener('progress', (event: ProgressEvent) => {
      onProgress?.(event.loaded);
    });

    xhr.addEventListener('load', () => {
      if (settled) return;
      if (xhr.status >= 200 && xhr.status < 300) {
        if (
          expectedContentType !== undefined &&
          mediaTypeOf(xhr.getResponseHeader('Content-Type')) !==
            mediaTypeOf(expectedContentType)
        ) {
          const status = xhr.status;
          finish(() =>
            reject(new DownloadError(status, 'Unexpected response')),
          );
          return;
        }
        finish(() => resolve(xhr.response as Blob));
        return;
      }
      const status = xhr.status;
      void readErrorBody(xhr.response).then(body =>
        finish(() =>
          reject(new DownloadError(status, reasonFrom(body, status))),
        ),
      );
    });

    xhr.addEventListener('error', () =>
      finish(() =>
        reject(new DownloadError(0, 'A network error stopped the download.')),
      ),
    );

    xhr.addEventListener('abort', () =>
      finish(() => reject(new DownloadError(0, 'Download cancelled.', true))),
    );

    xhr.open('GET', url, true);
    for (const [name, value] of Object.entries(headers)) {
      xhr.setRequestHeader(name, value);
    }
    xhr.responseType = 'blob';
    // The session cookie rides along, as every other correspondence call does.
    xhr.withCredentials = true;
    xhr.send();
  });

  return {
    promise,
    abort: () => xhr.abort(),
  };
};
